import { useRef, useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    SCROLLABLE_CONTENT_CLASS_NAME,
    Button,
    TSTFilterBar,
    TSTTable,
    TSTPagination,
} from "@impero-com/ui";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { Drawer } from "antd";
import { cx } from "class-variance-authority";
import { UseFieldArrayAppend } from "react-hook-form";

import type { FieldForSampleConclusionFieldKindAndSampleConclusionAnswerKind } from "api/generated/model";

import { SAMPLES_KEY, TestingActivityForm } from "../../schema";
import { ControlSample } from "../../types";
import { toDefaultActivitySampleValue } from "../../utils";

import * as theme from "./AddSamplesTable.css";
import { useAddSamplesTable } from "./useAddSamplesTable";

interface AddSamplesTableProps {
    open: boolean;
    onClose: () => void;
    controlSamples: ControlSample[];
    append: UseFieldArrayAppend<TestingActivityForm, typeof SAMPLES_KEY>;
    sampleConclusionFields: FieldForSampleConclusionFieldKindAndSampleConclusionAnswerKind[];
}

export function AddSamplesTable({
    open,
    onClose,
    controlSamples,
    append,
    sampleConclusionFields,
}: AddSamplesTableProps) {
    const { t } = useTranslation();

    const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
    const selectedSampleIds = Object.keys(selectedRows);

    const closeAndClearSelect = () => {
        setSelectedRows({});
        onClose();
    };

    const handleSelect = () => {
        const activitySamplesToAdd = controlSamples
            .filter((sample) => selectedSampleIds.includes(sample.activityId))
            .map((controlSample) =>
                toDefaultActivitySampleValue(
                    sampleConclusionFields,
                    controlSample.activityId
                )
            );
        append(activitySamplesToAdd);
        closeAndClearSelect();
    };

    const buttons = (
        <div className={theme.drawerButtons}>
            <Button
                disabled={selectedSampleIds.length === 0}
                size="large"
                variant="primary"
                onClick={handleSelect}
            >
                {t("ControlTestingActivityView_SamplesDrawer_Select")}
            </Button>

            <Button
                size="large"
                variant="default"
                onClick={closeAndClearSelect}
            >
                {t("ControlTestingActivityView_SamplesDrawer_Cancel")}
            </Button>
        </div>
    );

    return (
        <Drawer
            title={t("ControlTestingActivityView_SamplesDrawer_Title")}
            placement="bottom"
            open={open}
            onClose={closeAndClearSelect}
            size="large"
            closeIcon={null}
            extra={buttons}
            bodyStyle={{ padding: 0 }}
        >
            <SelectSamplesTable
                controlSamples={controlSamples}
                selectedRows={selectedRows}
                onSelectedRowsChange={setSelectedRows}
            />
        </Drawer>
    );
}

interface SelectSamplesTableProps {
    controlSamples: ControlSample[];
    selectedRows: RowSelectionState;
    onSelectedRowsChange: OnChangeFn<RowSelectionState>;
}

function SelectSamplesTable({
    controlSamples,
    selectedRows,
    onSelectedRowsChange,
}: SelectSamplesTableProps) {
    const { t } = useTranslation();

    const { table, getFilterComponent } = useAddSamplesTable({
        data: controlSamples,
        onSelectedRowsChange,
        selectedRows,
    });

    const tableRef = useRef<HTMLTableElement>(null);
    return (
        <div className={cx(theme.root, SCROLLABLE_CONTENT_CLASS_NAME)}>
            <TSTFilterBar<ControlSample>
                table={table}
                addLabel={t("FilterBar_AddFilter")}
                globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
                getFilterCountLabel={(count) =>
                    t("FilterBar_FiltersApplied", { count })
                }
                rowCountLabel={t("FilterBar_RowCount", {
                    count: table.getPrePaginationRowModel().rows.length,
                })}
                hideFiltersLabel={t("FilterBar_HideFilters")}
                clearAllLabel={t("FilterBar_ClearAll")}
                getFilterComponent={getFilterComponent}
            />
            <TSTTable
                tableRef={tableRef}
                table={table}
                noResultsFallback={t("NoResults")}
                noSourceRowsFallback={t("NoResults")}
            />
            <TSTPagination
                tableElement={tableRef}
                table={table}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
            />
        </div>
    );
}
